/**
 * API client for AI Interviewer endpoints.
 * Uses REACT_APP_BACKEND_URL from environment.
 *
 * All endpoints return JSON (or throw Error with the backend's detail message).
 * Pattern matches frontend/src/lib/api.js — credentials: 'include' for session cookies.
 */

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Per-session candidate share_token (from /interview/by-id/<id>?token=...).
// Set once by InterviewResumePage; attached as Authorization: Bearer on every
// subsequent API call so the candidate stays authenticated across requests.
let candidateToken = null;
export function setCandidateToken(token) {
  candidateToken = token || null;
}

async function apiFetch(path, options = {}) {
  const url = `${API_BASE}${path}`;
  const headers = { 'Content-Type': 'application/json', ...(options.headers || {}) };
  if (candidateToken && !headers.Authorization) {
    headers.Authorization = `Bearer ${candidateToken}`;
  }
  const res = await fetch(url, {
    credentials: 'include',
    ...options,
    headers,
  });

  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    const error = new Error(err.detail || 'Interview API request failed');
    error.status = res.status;
    throw error;
  }

  // Handle 204 No Content
  if (res.status === 204) return null;

  // Some endpoints return null (e.g. /next when done, /result before completion)
  const text = await res.text();
  if (!text) return null;
  return JSON.parse(text);
}

export const startInterview = (applicationId) =>
  apiFetch('/api/interviews/start', {
    method: 'POST',
    body: JSON.stringify({ application_id: applicationId }),
  });

export const getInterview = (interviewId) =>
  apiFetch(`/api/interviews/${encodeURIComponent(interviewId)}`);

export const getNextQuestion = (interviewId) =>
  apiFetch(`/api/interviews/${encodeURIComponent(interviewId)}/next`);

export const submitResponse = (interviewId, answerText) =>
  apiFetch(`/api/interviews/${encodeURIComponent(interviewId)}/respond`, {
    method: 'POST',
    body: JSON.stringify({ answer_text: answerText }),
  });

export const completeInterview = (interviewId) =>
  apiFetch(`/api/interviews/${encodeURIComponent(interviewId)}/complete`, {
    method: 'POST',
  });

export const getInterviewResult = (interviewId) =>
  apiFetch(`/api/interviews/${encodeURIComponent(interviewId)}/result`);

// Fetch question audio (MP3) and return a Blob URL ready for an <audio> src.
// Returns null on error so the UI can fall back to text-only silently.
export async function getQuestionAudioBlobUrl(interviewId, questionId) {
  const url = `${API_BASE}/api/interviews/${encodeURIComponent(interviewId)}/questions/${encodeURIComponent(questionId)}/audio`;
  const headers = {};
  if (candidateToken) headers.Authorization = `Bearer ${candidateToken}`;
  const res = await fetch(url, { credentials: 'include', headers });
  if (!res.ok) return null;
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

// Mint a LiveKit room-join JWT for the candidate via the share-token-scoped
// backend endpoint. Returns { url, token, room } where:
//   url   = wss://meet.dutyai.in (the self-hosted LiveKit Server)
//   token = HS256 JWT scoped to ONE room (interview_<id>)
//   room  = the room name to pass to <LiveKitRoom>
// Throws on 503 (LiveKit not configured) or 403 (token mismatch).
export const getAgentToken = (interviewId) =>
  apiFetch(`/api/interviews/${encodeURIComponent(interviewId)}/agent-token`, {
    method: 'POST',
  });

// Submit a coding-question answer. This is LIVE, not a stub: the endpoint
// proxies the source to the self-hosted **Piston** sandbox (Judge0 was
// evaluated and not chosen) and returns real
// {stdout, stderr, status, time_ms, memory_kb}.
//
// `action` is "save" for autosaved drafts and "run" for execution.
export const submitCodeRun = (interviewId, { language, source_code, action }) =>
  apiFetch(`/api/interviews/${encodeURIComponent(interviewId)}/code-submission`, {
    method: 'POST',
    body: JSON.stringify({ language, source_code, action }),
  });

// Upload a recorded audio Blob to /transcribe and return the transcript text.
// Throws on error; the caller is expected to surface a friendly message.
export async function transcribeAudio(interviewId, questionId, audioBlob) {
  const url = `${API_BASE}/api/interviews/${encodeURIComponent(interviewId)}/questions/${encodeURIComponent(questionId)}/transcribe`;
  const headers = {};
  if (candidateToken) headers.Authorization = `Bearer ${candidateToken}`;
  const form = new FormData();
  form.append('audio', audioBlob, 'answer.webm');
  const res = await fetch(url, { method: 'POST', credentials: 'include', headers, body: form });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || 'Transcription failed');
  }
  return await res.json();
}
